import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, Clock, Mail, Instagram, Phone, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import CalendlyEmbed from "@/components/CalendlyEmbed";
import LeadForm from "@/components/LeadForm";
import { PHONE_HREF, PHONE_DISPLAY, WHATSAPP_HREF, sendEvent } from "@/lib/tracking";

export default function Contact() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const prefill = location.state && location.state.prefillMessage;
    if (prefill) {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="contact-form-message"]');
        if (el) { el.value = prefill; el.dispatchEvent(new Event("input", { bubbles: true })); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
      }, 400);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  return (
    <div>
      <Seo
        title="Contact Kore Wellbeing — Bowness-on-Windermere, Cumbria"
        description="Get in touch with Kore Wellbeing at Rowan House, Beresford Road, Bowness-on-Windermere LA23 2JG. Enquire about Group Coaching, gym membership, day passes, space hire or a consultation."
        path="/contact"
      />
      <section className="container-kore pt-16 pb-6">
        <span className="eyebrow">Contact</span>
        <h1 className="display-1 mt-4 text-kore-forest">Say hello.</h1>
        <p className="mt-6 text-lg text-kore-ink/75 max-w-xl">Call, WhatsApp, email or drop us a message — whichever suits you. We reply personally within 24 hours.</p>
      </section>
      <section className="container-kore py-14 grid md:grid-cols-2 gap-14">
        <div data-testid="contact-form-card" className="bg-white rounded-3xl p-10 shadow-soft">
          <div className="eyebrow mb-6">Send a message</div>
          <LeadForm funnel="contact" submitLabel="Send message" testId="contact-form" showGoal={false}
            extraFields={[{ key: "message", label: "Your message", type: "textarea", required: true, wide: true }]} />
        </div>

        <div className="space-y-8">
          <div className="rounded-3xl overflow-hidden border border-kore-gold/20 aspect-[16/10]">
            <iframe
              title="Kore Wellbeing on the map"
              src="https://www.google.com/maps?q=Kore+Wellbeing,+Rowan+House,+Beresford+Road,+Bowness-on-Windermere+LA23+2JG&z=17&hl=en&output=embed"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><MapPin size={13} /> Visit</div>
              <address className="mt-2 text-kore-ink/85 not-italic leading-relaxed">
                Kore Wellbeing Ltd<br />
                Rowan House<br />
                Beresford Road<br />
                Bowness-on-Windermere<br />
                LA23 2JG<br />
                <span className="text-kore-ink/55 text-xs">Visits by arrangement</span>
              </address>
            </div>
            <div>
              <div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><Clock size={13} /> Hours</div>
              <p className="mt-2 text-kore-ink/85 leading-relaxed">
                Open gym: Mon &amp; Wed 09:00 – 18:00<br />
                Tue &amp; Fri 09:00 – 14:00 · Thu 11:00 – 18:00<br />
                Sat 08:30 – 14:00 · Sun 08:30 – 12:00<br />
                <span className="text-kore-ink/60">Group coaching from 06:00 weekdays</span>
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><Mail size={13} /> Email</div>
              <a href="mailto:info@korewellbeing.com" data-testid="contact-email-link" className="mt-2 block text-kore-ink/85 hover:text-kore-forest">info@korewellbeing.com</a>
            </div>
            <div>
              <div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><Phone size={13} /> Call</div>
              <a href={PHONE_HREF} data-testid="contact-phone-link" onClick={() => sendEvent("phone_click", { funnel: "contact", label: "contact_page" })} className="mt-2 block text-kore-ink/85 hover:text-kore-forest">{PHONE_DISPLAY}</a>
            </div>
            <div>
              <div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><MessageCircle size={13} /> WhatsApp</div>
              <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" onClick={() => sendEvent("whatsapp_click", { funnel: "contact", label: "contact_page" })} className="mt-2 block text-kore-ink/85 hover:text-kore-forest">Message us</a>
            </div>
            <div>
              <div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><Instagram size={13} /> Instagram</div>
              <a href="https://instagram.com/kore_wellbeing" target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="mt-2 block text-kore-ink/85 hover:text-kore-forest">@kore_wellbeing</a>
            </div>
          </div>
        </div>
      </section>

      {/* CALENDLY CONSULTATION BOOKING */}
      <section data-testid="calendly-consult-section" className="bg-kore-forest text-kore-warm py-24">
        <div className="container-kore">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="eyebrow text-kore-light">Book a free consultation</span>
            <h2 className="display-2 mt-4">Pick a time that suits you.</h2>
            <p className="mt-6 text-kore-warm/75 leading-relaxed">
              A relaxed 30-minute conversation with the KORE team. No pressure, no pitch — we'll listen to your goals and help you decide the right starting point.
            </p>
          </div>
          <div className="max-w-4xl mx-auto rounded-3xl bg-white overflow-hidden shadow-soft">
            <CalendlyEmbed
              utm={{ utmSource: "korewellbeing.com", utmMedium: "web", utmCampaign: "contact-page" }}
              height={760}
              bookingType="general_consultation"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
